import { gsap } from 'gsap';
export default class ConveyorCarousel {
  constructor(element, options = {}) {
    this.element = element;
    this.track = element?.querySelector('.carousel__track');
    this.ready = false;

    if (!this.track) return;

    this.options = {
      speed: 60, // vitesse de défilement auto (px/s)
      stopDuration: 0.7, // durée du ralentissement au survol (s)
      startDuration: 1, // durée de la reprise après survol (s)
      inertiaSmooth: 0.12, // 0 à 1 : plus petit = plus d'inertie après le lancer
      dragThreshold: 5, // px avant qu'un clic devienne un drag
      ...options,
    };

    // État
    this.state = { velocity: 1 }; // 1 = pleine vitesse, 0 = arrêté
    this.x = 0;
    this.setWidth = 0;
    this.inertiaTarget = 0; // distance (px) restante après un lancer

    // Drag
    this.pointerDown = false;
    this.dragging = false;
    this.suppressClick = false;
    this.dragStartX = 0;
    this.lastPointerX = 0;
    this.lastPointerTime = 0;
    this.dragVelocity = 0; // px par ms

    // Bind
    this.tick = this.tick.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = (e) => e.preventDefault();

    this.originals = [...this.track.children];

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.ro = new ResizeObserver(() => {
      clearTimeout(this.roTimer);
      this.roTimer = setTimeout(this.onResize, 100);
    });
    this.originals.forEach((el) => this.ro.observe(el));

    this.build();
    this.bindEvents();
    gsap.ticker.add(this.tick);
    this.ready = true;
  }

  /* ---------- Construction ---------- */

  build() {
    // Retire les anciens clones
    this.track.querySelectorAll('[data-clone]').forEach((el) => el.remove());

    const addSet = () =>
      this.originals.map((card) => {
        const clone = card.cloneNode(true);
        clone.dataset.clone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        this.track.appendChild(clone);
        return clone;
      });

    // La 1re copie sert à mesurer la vraie période de la boucle
    const [firstClone] = addSet();
    this.setWidth = firstClone.offsetLeft - this.originals[0].offsetLeft;
    if (this.setWidth <= 0) return;

    // Assez de copies pour couvrir le viewport + une période
    let guard = 0;
    while (
      this.track.offsetWidth < this.element.offsetWidth + this.setWidth &&
      guard++ < 10
    ) {
      addSet();
    }
  }

  bindEvents() {
    // Survol (souris seulement, pour éviter le faux survol après un tap)
    this.element.addEventListener('pointerenter', this.onPointerEnter);
    this.element.addEventListener('pointerleave', this.onPointerLeave);

    // Clavier : pause quand une carte a le focus
    this.element.addEventListener('focusin', this.onEnter);
    this.element.addEventListener('focusout', this.onLeave);

    // Drag
    this.element.addEventListener('pointerdown', this.onPointerDown);
    this.element.addEventListener('click', this.onClick, true); // capture
    this.element.addEventListener('dragstart', this.onDragStart);

    window.addEventListener('resize', this.onResize);
  }

  /* ---------- Boucle principale ---------- */

  tick(time, deltaTime) {
    const dt = deltaTime / 1000;

    // Défilement auto (ralenti au survol, coupé pendant un drag)
    let move = this.dragging
      ? 0
      : this.options.speed * this.state.velocity * dt;

    // Inertie du lancer, indépendante du framerate
    const ease =
      1 - Math.pow(1 - this.options.inertiaSmooth, deltaTime / 16.67);
    const inertiaMove = this.inertiaTarget * ease;
    this.inertiaTarget -= inertiaMove;
    move += inertiaMove;

    this.x -= move;
    this.wrap();

    gsap.set(this.track, { x: this.x });
  }

  wrap() {
    while (this.x <= -this.setWidth) this.x += this.setWidth;
    while (this.x > 0) this.x -= this.setWidth;
  }

  /* ---------- Survol / focus ---------- */

  onPointerEnter(e) {
    if (e.pointerType === 'mouse') this.onEnter();
  }

  onPointerLeave(e) {
    if (e.pointerType === 'mouse') this.onLeave();
  }

  onEnter() {
    gsap.to(this.state, {
      velocity: 0,
      duration: this.options.stopDuration,
      ease: 'power3.out',
      overwrite: true,
    });
  }

  onLeave() {
    gsap.to(this.state, {
      velocity: 1,
      duration: this.options.startDuration,
      ease: 'power2.inOut',
      overwrite: true,
    });
  }

  /* ---------- Drag ---------- */

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this.pointerDown = true;
    this.dragging = false;
    this.suppressClick = false;
    this.dragStartX = this.lastPointerX = e.clientX;
    this.lastPointerTime = performance.now();
    this.dragVelocity = 0;
    this.inertiaTarget = 0; // on "attrape" la piste : coupe l'inertie en cours

    // Sur window pour continuer à suivre si le curseur sort du carrousel
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(e) {
    if (!this.pointerDown) return;

    // En dessous du seuil, c'est encore un clic
    if (!this.dragging) {
      if (Math.abs(e.clientX - this.dragStartX) < this.options.dragThreshold) {
        return;
      }
      this.dragging = true;
      this.element.classList.add('is-dragging');
    }

    const now = performance.now();
    const dx = e.clientX - this.lastPointerX;
    const dt = Math.max(now - this.lastPointerTime, 1);

    // Vitesse lissée pour un lancer plus naturel
    this.dragVelocity = this.dragVelocity * 0.7 + (dx / dt) * 0.3;

    this.x += dx;
    this.wrap();
    gsap.set(this.track, { x: this.x });

    this.lastPointerX = e.clientX;
    this.lastPointerTime = now;
  }

  onPointerUp() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    if (this.dragging) {
      this.suppressClick = true;
      this.element.classList.remove('is-dragging');

      // Si le curseur s'est arrêté avant de relâcher, pas d'élan
      const idle = performance.now() - this.lastPointerTime;
      const v = idle > 80 ? 0 : this.dragVelocity;

      // Signe inversé : inertiaTarget positif = déplacement vers la gauche
      this.inertiaTarget = (-v * 16.67) / this.options.inertiaSmooth;
    }

    this.pointerDown = false;
    this.dragging = false;
  }

  onClick(e) {
    // Après un drag, on bloque le clic qui suivrait sur le lien de la carte
    if (this.suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      this.suppressClick = false;
    }
  }

  /* ---------- Resize / destroy ---------- */

  onResize() {
    if (!this.ready) return;
    this.build();
    this.x = this.x % this.setWidth;
  }

  destroy() {
    gsap.ticker.remove(this.tick);
    gsap.killTweensOf(this.state);

    this.element.removeEventListener('pointerenter', this.onPointerEnter);
    this.element.removeEventListener('pointerleave', this.onPointerLeave);
    this.element.removeEventListener('focusin', this.onEnter);
    this.element.removeEventListener('focusout', this.onLeave);
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    this.element.removeEventListener('click', this.onClick, true);
    this.element.removeEventListener('dragstart', this.onDragStart);

    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('resize', this.onResize);

    this.ro?.disconnect();
    clearTimeout(this.roTimer);
  }
}

/* ---------- Initialisation ---------- */

const carousel = new ConveyorCarousel(
  document.querySelector('#projectCarousel'),
  { speed: 50 },
);

// Remesure une fois les images chargées (les largeurs peuvent changer)
window.addEventListener('load', () => carousel.onResize());
